import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Authentification } from '../../Services/authentification';
import { InterventionApi } from '../../Services/intervention-api';
import { InterventionActualisation } from '../../Services/intervention-actualisation';
import {
  InterventionEditable,
  peutSupprimerIntervention,
} from '../../Services/intervention-edition';
import { messageErreurApi } from '../../Services/api-error';

/** Confirme la cible puis attend le 204 ; les refus conservent le dialogue sans actualiser les listes. */
@Component({
  selector: 'app-intervention-suppression-dialog',
  template: `
    <h2 id="suppression-intervention-titre">
      {{
        data.nature === 'bon'
          ? 'Supprimer ce bon d’intervention ?'
          : 'Supprimer cette planification ?'
      }}
    </h2>
    <p class="cible">Intervention n° {{ data.id }}</p>
    @if (data.valeurs.description) {
      <p class="description">{{ data.valeurs.description }}</p>
    }
    <p id="suppression-intervention-consequences">
      Cette suppression est définitive. L’intervention et ses photos seront supprimées. Le ticket,
      son historique et son état seront conservés.
    </p>
    @if (erreur()) {
      <p class="erreur" role="alert">{{ erreur() }}</p>
    }
    @if (!autorisee()) {
      <p class="erreur" role="alert">Vous n’avez plus le droit de supprimer cette intervention.</p>
    }
    @if (enCours()) {
      <p role="status">Suppression en cours…</p>
    }
    <div class="actions" [attr.aria-busy]="enCours()">
      <button type="button" data-annuler-suppression [disabled]="enCours()" (click)="annuler()">
        Annuler
      </button>
      <button
        type="button"
        class="supprimer"
        [disabled]="enCours() || !autorisee()"
        (click)="confirmer()"
      >
        Supprimer définitivement
      </button>
    </div>
  `,
  styles: `
    :host {
      display: block;
      padding: 24px;
      overflow: auto;
      max-height: calc(100dvh - 24px);
      color: #27312d;
    }
    h2 {
      margin: 0 0 16px;
      font-size: 1.35rem;
    }
    p {
      line-height: 1.5;
    }
    .cible {
      font-weight: 700;
    }
    .description {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      max-height: 120px;
      overflow: auto;
    }
    .erreur {
      color: #9b2222;
    }
    .actions {
      display: flex;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 24px;
    }
    button {
      min-height: 44px;
      padding: 10px 16px;
      border: 1px solid #52645c;
      border-radius: 8px;
      color: #27312d;
      background: white;
      font: inherit;
      cursor: pointer;
    }
    button.supprimer {
      background: #a22626;
      border-color: #a22626;
      color: white;
    }
    button:disabled {
      opacity: 0.6;
      cursor: default;
    }
    button:focus-visible {
      outline: 3px solid #15334b;
      outline-offset: 3px;
    }
  `,
})
export class InterventionSuppressionDialog {
  readonly data = inject<InterventionEditable>(MAT_DIALOG_DATA);
  private readonly dialog =
    inject<MatDialogRef<InterventionSuppressionDialog, boolean>>(MatDialogRef);
  private readonly api = inject(InterventionApi);
  private readonly actualisation = inject(InterventionActualisation);
  private readonly auth = inject(Authentification);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly notification = inject(MatSnackBar);
  private readonly session = this.auth.sessionVersion;
  private fermee = false;
  readonly enCours = signal(false);
  readonly erreur = signal('');
  readonly autorisee = computed(() =>
    peutSupprimerIntervention(this.auth.tokenInfoSignal(), this.data),
  );

  constructor() {
    // Aucune réponse tardive ne doit agir sur une nouvelle page ou une autre session.
    this.auth.sessionChanges.pipe(takeUntilDestroyed()).subscribe(() => this.fermer());
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.fermer());
    this.dialog
      .beforeClosed()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.fermee = true;
      });
  }

  annuler(): void {
    if (!this.enCours()) this.fermer();
  }

  async confirmer(): Promise<void> {
    if (!this.actif() || this.enCours() || !this.autorisee()) return;
    this.enCours.set(true);
    this.erreur.set('');
    this.dialog.disableClose = true;
    try {
      await this.api.supprimer(this.data);
      if (!this.actif()) return;
      this.actualisation.notifier(
        Number(this.data.valeurs.installationId),
        Number(this.data.valeurs.ticketId) || null,
      );
      this.notification.open('Intervention supprimée.', 'Fermer', { duration: 5000 });
      this.fermee = true;
      this.dialog.close(true);
    } catch (erreur) {
      if (this.actif())
        this.erreur.set(messageErreurApi(erreur, 'Impossible de supprimer l’intervention.'));
    } finally {
      if (this.actif()) {
        this.enCours.set(false);
        this.dialog.disableClose = false;
      }
    }
  }

  private actif(): boolean {
    return !this.fermee && !this.destroyRef.destroyed && this.session === this.auth.sessionVersion;
  }

  private fermer(): void {
    this.fermee = true;
    this.dialog.close();
  }
}
